import type { AttendanceRecord, ExpectedAttendee } from '../types';

export interface DisplayRosterRow {
  key: string;
  display: string;
  sub: string;
  status: 'Checked In' | 'Pending' | 'Not Coming' | 'Invalidated';
  category: 'Internal' | 'Guest' | 'Unknown';
  time?: string | null;
  method?: 'QR' | 'Manual' | 'Legacy' | null;
  type: 'Expected' | 'Additional';
  attendeeEmail?: string | null;
  departmentOrg?: string | null;
  attendance?: AttendanceRecord;
  expected?: ExpectedAttendee;
  reason?: string | null;
  reasonDetail?: string | null;
}

function normalizedIdentity(value: string): string {
  return value.trim().toLowerCase();
}

function isEmailIdentity(value: string): boolean {
  return value.includes('@');
}

function attendanceStatus(record: AttendanceRecord): 'Checked In' | 'Invalidated' {
  return record.is_valid === false ? 'Invalidated' : 'Checked In';
}

/**
 * Builds the Host/Co-Host roster without allowing a planning relationship to
 * override attendance. Email is the only authoritative cross-table identity;
 * free-text names deliberately remain independent to avoid false matches.
 */
export function buildHostRoster(expected: ExpectedAttendee[], attendance: AttendanceRecord[]): DisplayRosterRow[] {
  const attendanceByEmail = new Map<string, AttendanceRecord>();
  for (const record of attendance) {
    const email = normalizedIdentity(record.user_email);
    const existing = attendanceByEmail.get(email);
    if (!existing || (existing.is_valid === false && record.is_valid !== false)) {
      attendanceByEmail.set(email, record);
    }
  }

  const representedAttendance = new Set<string>();
  const representedExpectedEmails = new Set<string>();
  const rows: DisplayRosterRow[] = [];

  for (const item of expected) {
    const value = item.name_or_email.trim();
    const email = isEmailIdentity(value) ? normalizedIdentity(value) : null;

    // Migration 032 suppresses duplicate email identities. Keep the renderer
    // deterministic and safe if historical or malformed input still contains
    // more than one relationship row for the same authoritative identity.
    if (email && representedExpectedEmails.has(email)) continue;
    if (email) representedExpectedEmails.add(email);

    const match = email ? attendanceByEmail.get(email) : undefined;
    if (match) representedAttendance.add(match.record_id);
    const category = match
      ? (match.is_internal ? 'Internal' : 'Guest')
      : email
        ? (email.endsWith('@mtib.gov.my') ? 'Internal' : 'Guest')
        : 'Unknown';

    rows.push({
      key: `expected-${item.expected_id}`,
      display: match?.snapshot_name ?? value,
      sub: match?.snapshot_dept ?? (email ? value : 'Ahli dijemput'),
      status: match ? attendanceStatus(match) : item.effective_status === 'NOT_COMING' ? 'Not Coming' : 'Pending',
      category,
      time: match?.checkin_time,
      method: match?.checkin_method,
      type: 'Expected',
      attendeeEmail: match?.user_email ?? email,
      departmentOrg: match?.snapshot_dept ?? (email ? null : 'Ahli dijemput'),
      attendance: match,
      expected: item,
      reason: item.not_coming_reason,
      reasonDetail: item.not_coming_reason_detail,
    });
  }

  for (const record of attendance) {
    if (representedAttendance.has(record.record_id)) continue;
    rows.push({
      key: `attendance-${record.record_id}`,
      display: record.snapshot_name,
      sub: `${record.user_email} · ${record.snapshot_dept}`,
      status: attendanceStatus(record),
      category: record.is_internal ? 'Internal' : 'Guest',
      time: record.checkin_time,
      method: record.checkin_method,
      type: 'Additional',
      attendeeEmail: record.user_email,
      departmentOrg: record.snapshot_dept,
      attendance: record,
    });
  }

  return rows;
}
